import React, { useEffect, useRef, useState, useCallback } from "react";
import { Plus, Minus, Maximize2, Move, LogOut, Mail, Eye } from "lucide-react";
import DraggableCard from "../components/DraggableCard";
import MediaLightbox from "../components/MediaLightbox";
import Toolbar from "../components/Toolbar";
import EditableText from "../components/EditableText";
import StorageMeter from "../components/StorageMeter";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CANVAS_W = 1800;
const CANVAS_H = 1500;

// Build a mailto: link with prefilled subject + body asking about services.
// On Android (and iOS) this opens the user's default mail app — Gmail if
// installed and set as default — with everything pre-filled. Way more
// reliable than the Gmail web compose URL which can land you on a browser
// page asking you to sign in again.
function buildEmailLink(email) {
  const subject = "Project inquiry — your portfolio caught my eye";
  const body = [
    "Hi!",
    "",
    "I came across your portfolio and I'd love to chat about working together.",
    "",
    "A quick brief:",
    "• Project type:",
    "• Timeline:",
    "• Budget range:",
    "• Reference links:",
    "",
    "Looking forward to hearing from you!",
  ].join("\n");
  // mailto: requires RFC 3986 percent-encoding (spaces as %20). URLSearchParams
  // encodes spaces as '+' which Apple Mail / Gmail render literally — so use
  // encodeURIComponent which gives us %20.
  const q = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${email || ""}?${q}`;
}

// Renders the hero headline with the given accent word highlighted.
// Case-insensitive match; first occurrence only. Plain text otherwise.
function renderHeroHeadline(text, accent) {
  if (!text) return null;
  if (!accent) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(accent.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-[var(--vermillion)]">
        {text.slice(idx, idx + accent.length)}
      </span>
      {text.slice(idx + accent.length)}
    </>
  );
}

export default function Canvas({ mode = "view", onLogout }) {
  const isEdit = mode === "edit";
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const [cards, setCards] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  // Smaller initial zoom on phones so cards don't dominate the screen.
  const isPhoneInit = typeof window !== "undefined" && window.innerWidth < 640;
  const [zoom, setZoom] = useState(isPhoneInit ? 0.5 : 0.9);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const [storageBump, setStorageBump] = useState(0);
  const [visitCount, setVisitCount] = useState(null);
  const panStart = useRef(null);
  const patchTimers = useRef({});
  const profileTimer = useRef(null);

  // Initial fetch — retry a few times before giving up. Cloudflare's bot
  // challenge or a slow cold start can briefly abort requests; without retry
  // the user sees a misleading "Backend offline?" toast.
  const loadAll = useCallback(async () => {
    setLoading(true);
    let lastErr;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const [cardData, profileData] = await Promise.all([
          api.cards.list(),
          api.profile.get(),
        ]);
        setCards(cardData);
        setProfile(profileData);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        // exponential-ish backoff: 300ms, 700ms, 1500ms, 3000ms
        await new Promise((r) => setTimeout(r, 300 * Math.pow(2, attempt)));
      }
    }
    setLoading(false);
    toast("Couldn't reach the server. Tap to retry.", {
      action: { label: "Retry", onClick: () => loadAll() },
      duration: 8000,
    });
    // eslint-disable-next-line no-console
    console.error("Canvas load failed", lastErr);
  }, []);

  useEffect(() => {
    let alive = true;
    if (alive) loadAll();
    return () => {
      alive = false;
    };
  }, [loadAll]);

  // Center initial view once we know the wrapper size
  useEffect(() => {
    if (!wrapperRef.current) return;
    const w = wrapperRef.current.clientWidth;
    const h = wrapperRef.current.clientHeight;
    const initialZoom = w < 640 ? 0.5 : 0.9;
    setPan({
      x: (w - CANVAS_W * initialZoom) / 2,
      y: (h - CANVAS_H * initialZoom) / 2,
    });
  }, []);

  // Record one visit per browser session (visitor side only — admin sessions
  // don't bump the counter to avoid inflating the number).
  useEffect(() => {
    if (isEdit) return;
    const KEY = "studio_visit_counted_v1";
    if (sessionStorage.getItem(KEY)) {
      api.analytics.getVisits().then((d) => setVisitCount(d.total)).catch(() => {});
      return;
    }
    api.analytics
      .recordVisit()
      .then((d) => {
        sessionStorage.setItem(KEY, "1");
        setVisitCount(d.total);
      })
      .catch(() =>
        api.analytics.getVisits().then((d) => setVisitCount(d.total)).catch(() => {}),
      );
  }, [isEdit]);

  // Debounced server PATCH per card (admin only).
  const queuePatch = useCallback((id, patch) => {
    if (!isEdit) return;
    if (patchTimers.current[id]) clearTimeout(patchTimers.current[id]);
    patchTimers.current[id] = setTimeout(() => {
      api.cards.patch(id, patch).catch(() => toast("Save failed"));
      delete patchTimers.current[id];
    }, 350);
  }, [isEdit]);

  const updateCard = useCallback(
    (id, patch, persist = true) => {
      setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
      if (persist && isEdit) queuePatch(id, patch);
    },
    [isEdit, queuePatch],
  );

  // Visitors' drag is in-memory only — passed via persist=false path
  const visitorMove = useCallback((id, patch) => {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  const updateProfile = useCallback(
    (patch) => {
      if (!isEdit) return;
      setProfile((p) => ({ ...p, ...patch }));
      if (profileTimer.current) clearTimeout(profileTimer.current);
      profileTimer.current = setTimeout(() => {
        api.profile.patch(patch).catch(() => toast("Save failed"));
      }, 350);
    },
    [isEdit],
  );

  const deleteCard = useCallback(
    async (id) => {
      try {
        await api.cards.remove(id);
        setCards((prev) => prev.filter((c) => c.id !== id));
        setActiveId(null);
        toast("Card removed");
      } catch {
        toast("Delete failed");
      }
    },
    [],
  );

  const addCard = useCallback(
    async (preset) => {
      const base = {
        type: preset?.type || "image",
        category: preset?.category || (preset?.type === "video" ? "video" : "graphic"),
        src: preset?.src || "",
        thumbnail: preset?.thumbnail || "",
        title: preset?.title || "Untitled work",
        description:
          "Add a short description for this piece — context, role, tools, results.",
        tags: ["new"],
        client: "",
        year: String(new Date().getFullYear()),
        x: Math.max(40, 200 - pan.x / zoom),
        y: Math.max(40, 200 - pan.y / zoom),
        width: 320,
        height: 320,
        rotation: Math.random() * 6 - 3,
      };
      try {
        const created = await api.cards.create(base);
        setCards((p) => [...p, created]);
        setActiveId(created.id);
        toast("Card added — drag to reposition");
      } catch {
        toast("Couldn't add card");
      }
    },
    [pan, zoom],
  );

  // pan with empty canvas drag
  const onCanvasPointerDown = (e) => {
    if (e.target !== canvasRef.current && e.target !== wrapperRef.current) return;
    setPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onCanvasPointerMove = (e) => {
    if (!panning) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  };
  const onCanvasPointerUp = () => setPanning(false);

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom((z) => Math.min(2.2, Math.max(0.4, z - e.deltaY * 0.001)));
  };

  const activeCard = cards.find((c) => c.id === activeId) || null;

  return (
    <div
      className="relative w-screen h-screen overflow-hidden paper-grain select-none"
      style={{
        WebkitUserSelect: isEdit ? "auto" : "none",
        WebkitTouchCallout: isEdit ? "default" : "none",
      }}
    >
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 pointer-events-none">
        <div className="pointer-events-auto max-w-[80vw] sm:max-w-none">
          <EditableText
            as="div"
            className="font-display text-2xl sm:text-3xl tracking-wide leading-none uppercase"
            editable={isEdit}
            value={profile?.name || ""}
            onCommit={(v) => updateProfile({ name: v })}
            placeholder="Your name"
          />
          <div className="text-[10px] sm:text-xs text-ink-soft/80 mt-1 flex items-center gap-2">
            <EditableText
              as="span"
              className="font-serif-i"
              editable={isEdit}
              value={profile?.title || ""}
              onCommit={(v) => updateProfile({ title: v })}
              placeholder="What you do"
            />
          </div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2 flex-wrap justify-end max-w-full">
          {isEdit ? (
            <>
              <StorageMeter refreshKey={storageBump} />
              <div className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[var(--ink)]/15 bg-white/85 md:backdrop-blur px-3 py-1.5 text-[12px] tracking-wide text-[var(--ink)] card-stack">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <EditableText
                  as="span"
                  className="font-medium"
                  editable
                  value={profile?.contactLabel || ""}
                  onCommit={(v) => updateProfile({ contactLabel: v })}
                  placeholder="Email me"
                />
                <span className="opacity-50">·</span>
                <EditableText
                  as="span"
                  className="font-mono text-[12px]"
                  editable
                  value={profile?.contactEmail || ""}
                  onCommit={(v) => updateProfile({ contactEmail: v })}
                  placeholder="you@example.com"
                />
              </div>
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-[var(--vermillion)] text-white whitespace-nowrap">
                Edit mode
              </span>
              <Button variant="ghost" size="sm" onClick={onLogout} className="btn-pop h-8">
                <LogOut className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Exit</span>
              </Button>
            </>
          ) : (
            <a
              href={buildEmailLink(profile?.contactEmail)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 sm:gap-2 max-w-[80vw] rounded-full border border-[var(--ink)]/15 bg-white/85 md:backdrop-blur px-2.5 sm:px-3 py-1.5 text-[11px] sm:text-[12px] tracking-wide text-[var(--ink)] hover:bg-[var(--vermillion)] hover:text-white hover:border-transparent transition-colors btn-pop card-stack"
              title="Email me"
            >
              <Mail className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium hidden sm:inline">{profile?.contactLabel || "Email me"}</span>
              <span className="opacity-70 hidden sm:inline">·</span>
              <span className="font-mono text-[11px] sm:text-[12px] truncate">{profile?.contactEmail}</span>
            </a>
          )}
        </div>
      </header>

      {/* Hero copy. In view mode the whole block is pointer-events-none so the
          user can drag the canvas through it without selecting the text.
          In edit mode it stays interactive so the headline can be edited. */}
      <div
        className={`absolute top-[88px] sm:top-24 left-4 sm:left-6 z-10 max-w-[calc(100vw-2rem)] sm:max-w-md select-none ${
          isEdit ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={
          isEdit
            ? undefined
            : {
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                WebkitTouchCallout: "none",
              }
        }
        onSelectStart={isEdit ? undefined : (e) => e.preventDefault()}
      >
        {isEdit ? (
          <EditableText
            as="h1"
            className="font-display text-[clamp(1.9rem,7vw,5.2rem)] leading-[0.95] sm:leading-[0.9] text-[var(--ink)]"
            editable
            multiline
            value={profile?.heroHeadline || ""}
            onCommit={(v) => updateProfile({ heroHeadline: v })}
            placeholder="Your headline"
          />
        ) : (
          <h1 className="font-display text-[clamp(1.9rem,7vw,5.2rem)] leading-[0.95] sm:leading-[0.9] text-[var(--ink)] whitespace-pre-wrap">
            {renderHeroHeadline(profile?.heroHeadline || "", profile?.heroAccent || "")}
          </h1>
        )}
        {isEdit && (
          <div className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-soft/70 flex-wrap">
            <span>Accent word</span>
            <EditableText
              as="span"
              className="font-display text-base normal-case tracking-normal text-[var(--vermillion)] min-w-[40px] inline-block"
              editable
              value={profile?.heroAccent || ""}
              onCommit={(v) => updateProfile({ heroAccent: v })}
              placeholder="loud"
            />
          </div>
        )}
        <EditableText
          as="p"
          className="mt-3 sm:mt-4 text-xs sm:text-sm text-ink-soft/80 max-w-xs block"
          editable={isEdit}
          multiline
          value={profile?.tagline || ""}
          onCommit={(v) => updateProfile({ tagline: v })}
          placeholder="A short tagline visitors will see."
        />
      </div>

      {/* Canvas viewport */}
      <div
        ref={wrapperRef}
        className={`absolute inset-0 ${panning ? "cursor-grabbing" : "cursor-grab"}`}
        onPointerDown={onCanvasPointerDown}
        onPointerMove={onCanvasPointerMove}
        onPointerUp={onCanvasPointerUp}
        onPointerLeave={onCanvasPointerUp}
        onWheel={onWheel}
      >
        <div
          ref={canvasRef}
          className="absolute canvas-grid"
          style={{
            width: CANVAS_W,
            height: CANVAS_H,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
        >
          {cards.map((card) => (
            <DraggableCard
              key={card.id}
              card={card}
              editable={isEdit}
              zoom={zoom}
              onChange={(patch) =>
                isEdit ? updateCard(card.id, patch) : visitorMove(card.id, patch)
              }
              onOpen={() => setActiveId(card.id)}
            />
          ))}
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="font-display text-2xl text-ink-soft/40 animate-pulse">
            Loading canvas…
          </div>
        </div>
      )}

      {/* Bottom toolbar */}
      <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 max-w-[calc(100vw-1rem)]">
        <div className="flex items-center gap-1 rounded-full bg-white/90 md:backdrop-blur px-1.5 sm:px-2 py-1 sm:py-1.5 border border-[var(--ink)]/10 card-stack">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-full btn-pop"
            onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))}
          >
            <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
          <span className="text-[10px] sm:text-xs tabular-nums w-9 sm:w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-full btn-pop"
            onClick={() => setZoom((z) => Math.min(2.2, z + 0.1))}
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
          <div className="w-px h-5 bg-[var(--ink)]/15 mx-1 hidden sm:block" />
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-full btn-pop hidden sm:inline-flex"
            onClick={() => {
              const w = wrapperRef.current?.clientWidth || 1920;
              const h = wrapperRef.current?.clientHeight || 800;
              const z = w < 640 ? 0.5 : 0.9;
              setZoom(z);
              setPan({
                x: (w - CANVAS_W * z) / 2,
                y: (h - CANVAS_H * z) / 2,
              });
            }}
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
        {isEdit && <Toolbar onAdd={addCard} onUploadDone={() => setStorageBump((n) => n + 1)} />}
      </div>

      <div className="hidden md:flex absolute bottom-6 right-6 text-[11px] uppercase tracking-[0.2em] text-ink-soft/60 z-20 items-center gap-2">
        <Move className="w-3 h-3" /> drag cards to play · empty space pans · ⌘ + scroll to zoom
      </div>

      {/* Visit counter chip — public page only */}
      {!isEdit && visitCount !== null && (
        <div data-testid="visit-counter-mobile" className="absolute bottom-3 sm:bottom-6 right-3 sm:right-6 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 border border-[var(--ink)]/10 card-stack text-[10px] sm:text-[11px] tabular-nums text-ink-soft md:hidden">
          <Eye className="w-3 h-3" /> {visitCount.toLocaleString()}
        </div>
      )}
      {!isEdit && visitCount !== null && (
        <div data-testid="visit-counter-desktop" className="hidden md:inline-flex absolute bottom-6 left-6 z-20 items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 border border-[var(--ink)]/10 card-stack text-[11px] tabular-nums text-ink-soft">
          <Eye className="w-3.5 h-3.5" />
          <span className="font-medium">{visitCount.toLocaleString()}</span>
          <span className="opacity-60">visits</span>
        </div>
      )}

      <MediaLightbox
        card={activeCard}
        editable={isEdit}
        onClose={() => setActiveId(null)}
        onChange={(patch) => activeCard && updateCard(activeCard.id, patch)}
        onDelete={() => activeCard && deleteCard(activeCard.id)}
      />
    </div>
  );
}
